import BaseBehavior from '../../../framework/base/base';
import { showElement, hideElement } from '../../../shared/utilities/show-hide';
import { screenSmallerThan } from '../../../shared/utilities/screenSize';

export default class Workday extends BaseBehavior {
	constructor(element) {
		super(element);
		this.initDomCache();
		this.addEventListeners();
		this.jobListingUrl = element.dataset.baseurl;
		this.jobFamilyURL = element.dataset.jobfamilyurl;
		this.readmoreText = element.dataset.readmoreText;
		this.columnKeys = this.element.dataset.columnKeys.split(','); // '' is for extra readmore column
		this.getFiltersFromStorage();
		this.getJobListingData(true);
	}

	getFiltersFromStorage() {
		if (window.sessionStorage) {
			Array.from(
				this.element.querySelectorAll('select:not([data-ref="workday__job-category"])')
			).forEach(filter => {
				const value = sessionStorage.getItem(filter.dataset.ref);
				if (value) {
					filter.value = value;
				}
			});
		}
	}

	getDeviceBasedDOM(isMobile = screenSmallerThan('sm')) {
		return {
			skeleton: isMobile ? this.dom.mobileSkeleton : this.dom.desktopSkeleton,
			body: isMobile ? this.dom.mobileBody : this.dom.desktopBody
		};
	}

	switchToBodyFromSkeleton() {
		const { body, skeleton } = this.getDeviceBasedDOM();
		// hide skeleton show body
		showElement(body);
		hideElement(skeleton);
	}

	switchToSkeletonFromBody() {
		const { body, skeleton } = this.getDeviceBasedDOM();
		// hide body show skeleton
		showElement(skeleton);
		hideElement(body);
	}

	fetchJobListingData(url, initialLoad) {
		hideElement(this.dom.noresult);
		hideElement(this.dom.errormsg);
		hideElement(this.dom.showMore);
		this.switchToSkeletonFromBody();

		return fetch(url)
			.then(response => {
				if (response.ok) {
					return response.text();
				}

				this.resetView();
				showElement(this.dom.errormsg);
				return undefined; // this is for fixing consistent return eslint error
			})
			.then(data => {
				const fulldata = JSON.parse(data).Report_Entry;

				if (fulldata.length === 0) {
					this.resetView();
					showElement(this.dom.noresult);
				} else {
					this.loadJobListingData(fulldata, true);
				}
				if (initialLoad && this.dom.jobCategory) {
					this.loadJobCategoryDropdown();
				}
			})
			.catch(error => {
				// eslint-disable-next-line no-console
				console.log(error);
			})
			.finally(() => {
				this.switchToBodyFromSkeleton();
			});
	}

	loadJobCategoryDropdown() {
		return fetch(this.jobFamilyURL)
			.then(response => {
				if (response.ok) {
					return response.text();
				}

				throw new Error('Something went wrong');
			})
			.then(data => {
				const jobCategoryData = JSON.parse(data).Report_Entry;
				this.populateJobCategoryData(jobCategoryData);
			});
	}

	addEventListeners() {
		this.addEventListener(this.element.querySelector('.dropdown-section'), 'change', evt => {
			const filter = evt.target.closest('select');
			if (filter) {
				if (window.sessionStorage) {
					const { ref } = filter.dataset;
					sessionStorage.setItem(ref, evt.target.value);
				}
				this.getJobListingData(false);
			}
		});

		this.addEventListener(this.dom.showMore, 'click', () => {
			this.showMoreRecords();
		});
	}

	showMoreRecords() {
		if (screenSmallerThan('sm')) {
			this.populateMobileData(this.jobListingData, this.columnKeys, false);
		} else {
			this.populateDesktopData(this.jobListingData, this.columnKeys, false);
		}
	}

	getJobListingData(initialLoad) {
		const hours = this.dom.hours?.value || 'Full_time,Part_time';
		const country = this.dom.country?.value || '';

		let jobCategory = ''; // in case job category filter is not selected from BE, then use default value.
		if (this.dom.jobCategory) {
			jobCategory =
				sessionStorage.getItem(this.dom.jobCategory.dataset.ref) || this.dom.jobCategory.value;
		}
		const url = `${this.element.dataset.baseurl}?country=${country}&workingHours=${hours}&category=${jobCategory}`;

		this.fetchJobListingData(url, initialLoad);
	}

	resetView() {
		if (screenSmallerThan('sm')) {
			this.element.querySelector('.table-container .row-container').innerHTML = '';
		} else {
			this.dom.desktopBody.innerHTML = '';
		}
		hideElement(this.dom.showMore);
	}

	getAnimationDelayStyle(index) {
		return `style="animation-delay: ${(0.1 * (index + 1)).toPrecision(1)}s"`;
	}

	populateJobCategoryData(data) {
		const jobCategory = sessionStorage.getItem(this.dom.jobCategory.dataset.ref);

		const fragment = document.createDocumentFragment();

		data.forEach(item => {
			const option = document.createElement('option');
			option.value = item.ID;
			option.innerHTML = item.JobCategoryName;
			fragment.appendChild(option);
		});

		this.dom.jobCategory.appendChild(fragment);
		if (jobCategory) {
			this.dom.jobCategory.value = jobCategory;
		}
	}

	loadColumnMobile(item, keys, columnNames) {
		let colString = ``;
		for (let i = 1; i < keys.length && keys[i] !== ''; i++) {
			colString += `<div class="expanded-table">
			<div class="v2-text-align--left v2-text--bold expanded-table__column-left v2-flexbox
				v2-align--middle">${columnNames[i]}</div>
			<div class="v2-text-align--right expanded-table__column-right">${item[keys[i]]}</div>
			</div>`;
		}
		return colString;
	}

	populateMobileData(data, keys, refresh) {
		const tableContainer = this.element.querySelector('.table-container');
		const rowContainer = tableContainer.querySelector('.row-container');
		const colNames = Array.from(this.dom.desktopHeader.querySelectorAll('th')).map(
			td => td.innerText
		);

		let rowString = ``;

		if (refresh) {
			rowContainer.innerHTML = '';
		}

		for (
			let i = this.currentCounter, j = 0;
			j < this.initialRecord && this.currentCounter < this.totalRecord;
			i++, j++
		) {
			this.currentCounter++;
			if (this.currentCounter === this.totalRecord) {
				hideElement(this.dom.showMore);
			}

			const animDelayStyle = this.getAnimationDelayStyle(j);
			rowString += `<div class="workday__row">
							<label class="v2-flexbox v2-align--middle v2-pav15 v2-sm-max-mah15" ${animDelayStyle}>
										<div class="first v2-text--bold">${data[i][keys[0]]}
										</div>
									</label>
									<div class="info" ${animDelayStyle}>
										<div class="info-content">${this.loadColumnMobile(data[i], keys, colNames)}
										</div>
									</div>
									<div class="cta readmore" ${animDelayStyle}>
										<a class="v2-btn v2-btn-secondary" href=${data[i].External_Job_URL} target="_blank">
										  ${this.readmoreText}
										</a>
									</div>
						</div>`;
		}
		rowContainer.insertAdjacentHTML('beforeend', rowString);
	}

	loadJobListingData(data, refresh) {
		this.totalRecord = data.length;
		this.initialRecord = +this.element.getAttribute('data-int-items');
		this.currentCounter = 0;
		this.jobListingData = data;

		if (screenSmallerThan('sm')) {
			this.populateMobileData(data, this.columnKeys, refresh);
		} else {
			this.populateDesktopData(data, this.columnKeys, refresh);
		}

		if (this.initialRecord < this.totalRecord) {
			showElement(this.dom.showMore);
		}

		this.switchToBodyFromSkeleton();
	}

	loadEachColumn(item, keys, rowIndex) {
		return keys
			.map(key => {
				const animDelayStyle = this.getAnimationDelayStyle(rowIndex);
				const cellValue = item[key] || '-';

				if (key === '') {
					return `<td class="readmore" ${animDelayStyle}>
						     <a class="v2-btn v2-btn-secondary" href=${item.External_Job_URL} target="_blank">${this.readmoreText}</a>
					       </td>`;
				}

				return `<td ${animDelayStyle}>${cellValue}</td>`;
			})
			.join('');
	}

	populateDesktopData(data, keys, refresh) {
		if (refresh) {
			this.dom.desktopBody.innerHTML = '';
		}

		let rowString = ``;
		for (
			let i = this.currentCounter, j = 0;
			j < this.initialRecord && this.currentCounter < this.totalRecord;
			i++, j++
		) {
			this.currentCounter++;

			if (this.currentCounter === this.totalRecord) {
				hideElement(this.dom.showMore);
			}
			rowString += `<tr>${this.loadEachColumn(data[i], keys, j)}</tr>`;
		}
		this.dom.desktopBody.insertAdjacentHTML('beforeend', rowString);
	}
}
